import { useI18n } from '@affine/i18n';
import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';

import { getAccessToken } from '../auth/portal-auth';
import { usePortalAuth } from '../auth/portal-auth-provider';
import * as styles from '../screens/portal.css';
import { PortalErpService } from './portal-erp';

// One-shot throttle: at most one automatic session-recovery per window, so a
// server that 401s fresh tokens (clock skew, misconfig) can never redirect-loop.
let lastRecovery = 0;
const RECOVERY_WINDOW_MS = 30_000;

const PortalErpContext = createContext<PortalErpService | null>(null);

/**
 * Provides a single {@link PortalErpService} bound to the PKCE token store so
 * 13b/13c screen containers can resolve it via {@link usePortalErp}.
 *
 * Brand isolation (owner 2026-08-26): when the server refuses the session as
 * the OTHER company's customer on this branded origin (`[WRONG-PORTAL]` 403),
 * the provider renders a BLOCKING notice pointing at the customer's own portal
 * address instead of the screens — never an auto-recovery loop, never a wall
 * of error panels.
 */
export function PortalErpProvider({ children }: { children: ReactNode }) {
  const { recoverExpiredSession, logout } = usePortalAuth();
  // undefined = not triggered; null = wrong portal with no known home origin.
  const [wrongPortalHome, setWrongPortalHome] = useState<
    string | null | undefined
  >(undefined);
  const service = useMemo(
    () =>
      new PortalErpService(
        getAccessToken,
        () => {
          const now = Date.now();
          if (now - lastRecovery > RECOVERY_WINDOW_MS) {
            lastRecovery = now;
            recoverExpiredSession();
          }
        },
        home => setWrongPortalHome(home)
      ),
    [recoverExpiredSession]
  );
  return (
    <PortalErpContext.Provider value={service}>
      {wrongPortalHome !== undefined ? (
        <WrongPortalNotice home={wrongPortalHome} onSignOut={logout} />
      ) : (
        children
      )}
    </PortalErpContext.Provider>
  );
}

/** The full-screen wrong-portal notice (blocking — replaces the screens). */
function WrongPortalNotice({
  home,
  onSignOut,
}: {
  home: string | null;
  onSignOut: () => void;
}) {
  const t = useI18n();
  return (
    <div className={styles.body} data-testid="portal-wrong-portal">
      <h1 className={styles.title}>
        {t['com.affine.pipe.portal.wrong-portal.title']()}
      </h1>
      <div className={styles.statusPanel} role="alert">
        <div>
          <p>{t['com.affine.pipe.portal.wrong-portal.body']()}</p>
          {home ? (
            <p>
              <a className={styles.verifyLink} href={home}>
                {t['com.affine.pipe.portal.wrong-portal.link']({ home })}
              </a>
            </p>
          ) : null}
          <p>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={onSignOut}
            >
              {t['com.affine.pipe.portal.nav.signout']()}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export function usePortalErp(): PortalErpService {
  const service = useContext(PortalErpContext);
  if (!service) {
    throw new Error('usePortalErp must be used within a PortalErpProvider');
  }
  return service;
}
